import { html, nothing } from "lit";
import type {
  AgentDefinitionEntry,
  AgentBindingMatch,
  IdentityFileInfo,
} from "../controllers/agent-definitions.ts";
import type { CronJobEntry } from "../controllers/igloobot.ts";
import type {
  AgentFileEntry,
  AgentsFilesListResult,
  AgentsListResult,
  AgentIdentityResult,
  ChannelAccountSnapshot,
  ChannelsStatusSnapshot,
  CronJob,
  CronStatus,
  SkillStatusEntry,
  SkillStatusReport,
} from "../types.ts";
import type { TaskEntry } from "./tasks-admin.ts";
import {
  expandToolGroups,
  normalizeToolName,
  resolveToolProfilePolicy,
} from "../../../../src/agents/tool-policy.js";
import { formatRelativeTimestamp } from "../format.ts";
import {
  formatCronPayload,
  formatCronSchedule,
  formatCronState,
  formatNextRun,
} from "../presenter.ts";

export type AgentsPanel = "overview" | "files" | "tools" | "skills" | "tasks" | "channels" | "cron";

export type CatalogModelEntry = {
  id: string;
  name: string;
  provider: string;
  contextWindow?: number;
  reasoning?: boolean;
};

export type AgentsProps = {
  loading: boolean;
  error: string | null;
  agentsList: AgentsListResult | null;
  selectedAgentId: string | null;
  activePanel: AgentsPanel;
  configForm: Record<string, unknown> | null;
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  modelCatalog: CatalogModelEntry[];
  channelsLoading: boolean;
  channelsError: string | null;
  channelsSnapshot: ChannelsStatusSnapshot | null;
  channelsLastSuccess: number | null;
  cronLoading: boolean;
  cronStatus: CronStatus | null;
  cronJobs: CronJob[];
  cronError: string | null;
  agentFilesLoading: boolean;
  agentFilesError: string | null;
  agentFilesList: AgentsFilesListResult | null;
  agentFileActive: string | null;
  agentFileContents: Record<string, string>;
  agentFileDrafts: Record<string, string>;
  agentFileSaving: boolean;
  agentIdentityLoading: boolean;
  agentIdentityError: string | null;
  agentIdentityById: Record<string, AgentIdentityResult>;
  agentSkillsLoading: boolean;
  agentSkillsReport: SkillStatusReport | null;
  agentSkillsError: string | null;
  agentSkillsAgentId: string | null;
  skillsFilter: string;
  // Agent Tasks
  agentTasksLoading: boolean;
  agentTasksError: string | null;
  agentTasks: TaskEntry[];
  onAgentTasksRefresh: () => void;
  // Agent Project Cron (read-only)
  agentProjectCronJobs: CronJobEntry[];
  agentProjectCronJobsLoading: boolean;
  // Dynamic Agent Definitions
  agentDefs: AgentDefinitionEntry[];
  agentDefsLoading: boolean;
  agentDefsError: string | null;
  agentDefCreateMode: boolean;
  agentDefEditingId: string | null;
  agentDefForm: Partial<AgentDefinitionEntry> | null;
  projects: Array<{ id: string; name: string; slug?: string }>;
  onAgentDefsRefresh: () => void;
  onAgentDefCreate: () => void;
  onAgentDefEdit: (def: AgentDefinitionEntry) => void;
  onAgentDefCancel: () => void;
  onAgentDefSave: () => void;
  onAgentDefDelete: (agentId: string) => void;
  onAgentDefToggle: (agentId: string, active: boolean) => void;
  onAgentDefFormChange: (field: string, value: unknown) => void;
  // Bindings (dynamic agents)
  onAgentDefBindingAdd: () => void;
  onAgentDefBindingRemove: (index: number) => void;
  onAgentDefBindingChange: (index: number, field: string, value: string) => void;
  // Identity file overrides (dynamic agents)
  agentDefIdFiles: IdentityFileInfo[];
  agentDefIdFilesLoading: boolean;
  agentDefIdFileActive: string | null;
  agentDefIdFileDrafts: Record<string, string>;
  agentDefIdFileSaving: boolean;
  onAgentDefIdFileSelect: (fileName: string) => void;
  onAgentDefIdFileDraftChange: (fileName: string, content: string) => void;
  onAgentDefIdFileSave: (agentId: string, fileName: string) => void;
  onAgentDefIdFileReset: (agentId: string, fileName: string) => void;
  onRefresh: () => void;
  onSelectAgent: (agentId: string) => void;
  onSelectPanel: (panel: AgentsPanel) => void;
  onLoadFiles: (agentId: string) => void;
  onSelectFile: (name: string) => void;
  onFileDraftChange: (name: string, content: string) => void;
  onFileReset: (name: string) => void;
  onFileSave: (name: string) => void;
  onToolsProfileChange: (agentId: string, profile: string | null, clearAllow: boolean) => void;
  onToolsOverridesChange: (agentId: string, alsoAllow: string[], deny: string[]) => void;
  onConfigReload: () => void;
  onConfigSave: () => void;
  onModelChange: (agentId: string, modelId: string | null) => void;
  onModelFallbacksChange: (agentId: string, fallbacks: string[]) => void;
  onChannelsRefresh: () => void;
  onCronRefresh: () => void;
  onSkillsFilterChange: (next: string) => void;
  onSkillsRefresh: () => void;
  onAgentSkillToggle: (agentId: string, skillName: string, enabled: boolean) => void;
  onAgentSkillsClear: (agentId: string) => void;
  onAgentSkillsDisableAll: (agentId: string) => void;
};

const TOOL_SECTIONS = [
  {
    id: "fs",
    label: "Files",
    tools: [
      { id: "read", label: "read", description: "Read file contents" },
      { id: "write", label: "write", description: "Create or overwrite files" },
      { id: "edit", label: "edit", description: "Make precise edits" },
      { id: "apply_patch", label: "apply_patch", description: "Patch files (OpenAI)" },
    ],
  },
  {
    id: "runtime",
    label: "Runtime",
    tools: [
      { id: "exec", label: "exec", description: "Run shell commands" },
      { id: "process", label: "process", description: "Manage background processes" },
    ],
  },
  {
    id: "web",
    label: "Web",
    tools: [
      { id: "web_search", label: "web_search", description: "Search the web" },
      { id: "web_fetch", label: "web_fetch", description: "Fetch web content" },
    ],
  },
  {
    id: "memory",
    label: "Memory",
    tools: [
      { id: "memory_search", label: "memory_search", description: "Semantic search" },
      { id: "memory_get", label: "memory_get", description: "Read memory files" },
    ],
  },
  {
    id: "sessions",
    label: "Sessions",
    tools: [
      { id: "sessions_list", label: "sessions_list", description: "List sessions" },
      { id: "sessions_history", label: "sessions_history", description: "Session history" },
      { id: "sessions_send", label: "sessions_send", description: "Send to session" },
      { id: "sessions_spawn", label: "sessions_spawn", description: "Spawn sub-agent" },
      { id: "session_status", label: "session_status", description: "Session status" },
      { id: "update_plan", label: "update_plan", description: "Track a step-by-step plan" },
    ],
  },
  {
    id: "ui",
    label: "UI",
    tools: [
      { id: "browser", label: "browser", description: "Control web browser" },
      { id: "canvas", label: "canvas", description: "Control canvases" },
    ],
  },
  {
    id: "messaging",
    label: "Messaging",
    tools: [{ id: "message", label: "message", description: "Send messages" }],
  },
  {
    id: "automation",
    label: "Automation",
    tools: [
      { id: "cron", label: "cron", description: "Schedule tasks" },
      { id: "gateway", label: "gateway", description: "Gateway control" },
    ],
  },
  {
    id: "nodes",
    label: "Nodes",
    tools: [{ id: "nodes", label: "nodes", description: "Nodes + devices" }],
  },
  {
    id: "agents",
    label: "Agents",
    tools: [{ id: "agents_list", label: "agents_list", description: "List agents" }],
  },
  {
    id: "media",
    label: "Media",
    tools: [{ id: "image", label: "image", description: "Image understanding" }],
  },
];

const PROFILE_OPTIONS = [
  { id: "minimal", label: "Minimal" },
  { id: "coding", label: "Coding" },
  { id: "messaging", label: "Messaging" },
  { id: "full", label: "Full" },
] as const;

type ToolPolicy = {
  allow?: string[];
  deny?: string[];
};

type AgentConfigEntry = {
  id: string;
  name?: string;
  workspace?: string;
  agentDir?: string;
  model?: unknown;
  skills?: string[];
  tools?: {
    profile?: string;
    allow?: string[];
    alsoAllow?: string[];
    deny?: string[];
  };
};

type ConfigSnapshot = {
  agents?: {
    defaults?: { workspace?: string; model?: unknown; models?: Record<string, { alias?: string }> };
    list?: AgentConfigEntry[];
  };
  tools?: {
    profile?: string;
    allow?: string[];
    alsoAllow?: string[];
    deny?: string[];
  };
};

function normalizeAgentLabel(agent: { id: string; name?: string; identity?: { name?: string } }) {
  return agent.name?.trim() || agent.identity?.name?.trim() || agent.id;
}

function isLikelyEmoji(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  if (trimmed.length > 16) {
    return false;
  }
  let hasNonAscii = false;
  for (let i = 0; i < trimmed.length; i += 1) {
    if (trimmed.charCodeAt(i) > 127) {
      hasNonAscii = true;
      break;
    }
  }
  if (!hasNonAscii) {
    return false;
  }
  if (trimmed.includes("://") || trimmed.includes("/") || trimmed.includes(".")) {
    return false;
  }
  return true;
}

function resolveAgentEmoji(
  agent: { identity?: { emoji?: string; avatar?: string } },
  agentIdentity?: AgentIdentityResult | null,
) {
  const identityEmoji = agentIdentity?.emoji?.trim();
  if (identityEmoji && isLikelyEmoji(identityEmoji)) {
    return identityEmoji;
  }
  const agentEmoji = agent.identity?.emoji?.trim();
  if (agentEmoji && isLikelyEmoji(agentEmoji)) {
    return agentEmoji;
  }
  const identityAvatar = agentIdentity?.avatar?.trim();
  if (identityAvatar && isLikelyEmoji(identityAvatar)) {
    return identityAvatar;
  }
  const avatar = agent.identity?.avatar?.trim();
  if (avatar && isLikelyEmoji(avatar)) {
    return avatar;
  }
  return "";
}

function agentBadgeText(agentId: string, defaultId: string | null) {
  return defaultId && agentId === defaultId ? "default" : null;
}

function formatBytes(bytes?: number) {
  if (bytes == null || !Number.isFinite(bytes)) {
    return "-";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ["KB", "MB", "GB", "TB"];
  let size = bytes / 1024;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size < 10 ? 1 : 0)} ${units[unitIndex]}`;
}

function resolveAgentConfig(config: Record<string, unknown> | null, agentId: string) {
  const cfg = config as ConfigSnapshot | null;
  const list = cfg?.agents?.list ?? [];
  const entry = list.find((agent) => agent?.id === agentId);
  return {
    entry,
    defaults: cfg?.agents?.defaults,
    globalTools: cfg?.tools,
  };
}

type AgentContext = {
  workspace: string;
  model: string;
  identityName: string;
  identityEmoji: string;
  skillsLabel: string;
  isDefault: boolean;
};

function buildAgentContext(
  agent: AgentsListResult["agents"][number],
  configForm: Record<string, unknown> | null,
  agentFilesList: AgentsFilesListResult | null,
  defaultId: string | null,
  agentIdentity?: AgentIdentityResult | null,
): AgentContext {
  const config = resolveAgentConfig(configForm, agent.id);
  const workspaceFromFiles =
    agentFilesList && agentFilesList.agentId === agent.id ? agentFilesList.workspace : null;
  const workspace =
    workspaceFromFiles || config.entry?.workspace || config.defaults?.workspace || "default";
  const modelLabel = config.entry?.model
    ? resolveModelLabel(config.entry?.model)
    : resolveModelLabel(config.defaults?.model);
  const identityName =
    agentIdentity?.name?.trim() ||
    agent.identity?.name?.trim() ||
    agent.name?.trim() ||
    config.entry?.name ||
    agent.id;
  const identityEmoji = resolveAgentEmoji(agent, agentIdentity) || "-";
  const skillFilter = Array.isArray(config.entry?.skills) ? config.entry?.skills : null;
  const skillCount = skillFilter?.length ?? null;
  return {
    workspace,
    model: modelLabel,
    identityName,
    identityEmoji,
    skillsLabel: skillFilter ? `${skillCount} selected` : "all skills",
    isDefault: Boolean(defaultId && agent.id === defaultId),
  };
}

function resolveModelLabel(model?: unknown): string {
  if (!model) {
    return "-";
  }
  if (typeof model === "string") {
    return model.trim() || "-";
  }
  if (typeof model === "object" && model) {
    const record = model as { primary?: string; fallbacks?: string[] };
    const primary = record.primary?.trim();
    if (primary) {
      const fallbackCount = Array.isArray(record.fallbacks) ? record.fallbacks.length : 0;
      return fallbackCount > 0 ? `${primary} (+${fallbackCount} fallback)` : primary;
    }
  }
  return "-";
}

function normalizeModelValue(label: string): string {
  const match = label.match(/^(.+) \(\+\d+ fallback\)$/);
  return match ? match[1] : label;
}

function resolveModelPrimary(model?: unknown): string | null {
  if (!model) {
    return null;
  }
  if (typeof model === "string") {
    const trimmed = model.trim();
    return trimmed || null;
  }
  if (typeof model === "object" && model) {
    const record = model as Record<string, unknown>;
    const candidate =
      typeof record.primary === "string"
        ? record.primary
        : typeof record.model === "string"
          ? record.model
          : typeof record.id === "string"
            ? record.id
            : typeof record.value === "string"
              ? record.value
              : null;
    const primary = candidate?.trim();
    return primary || null;
  }
  return null;
}

function resolveModelFallbacks(model?: unknown): string[] | null {
  if (!model || typeof model === "string") {
    return null;
  }
  if (typeof model === "object" && model) {
    const record = model as Record<string, unknown>;
    const fallbacks = Array.isArray(record.fallbacks)
      ? record.fallbacks
      : Array.isArray(record.fallback)
        ? record.fallback
        : null;
    return fallbacks
      ? fallbacks.filter((entry): entry is string => typeof entry === "string")
      : null;
  }
  return null;
}

type ConfiguredModelOption = {
  value: string;
  label: string;
};

function resolveConfiguredModels(
  configForm: Record<string, unknown> | null,
): ConfiguredModelOption[] {
  const cfg = configForm as ConfigSnapshot | null;
  const models = cfg?.agents?.defaults?.models;
  if (!models || typeof models !== "object") {
    return [];
  }
  const options: ConfiguredModelOption[] = [];
  for (const [modelId, modelRaw] of Object.entries(models)) {
    const trimmed = modelId.trim();
    if (!trimmed) {
      continue;
    }
    const alias =
      modelRaw && typeof modelRaw === "object" && "alias" in modelRaw
        ? typeof (modelRaw as { alias?: unknown }).alias === "string"
          ? (modelRaw as { alias?: string }).alias?.trim()
          : undefined
        : undefined;
    const label = alias && alias !== trimmed ? `${alias} (${trimmed})` : trimmed;
    options.push({ value: trimmed, label });
  }
  return options;
}

function buildModelOptions(
  configForm: Record<string, unknown> | null,
  current?: string | null,
  catalog?: CatalogModelEntry[],
) {
  const configOptions = resolveConfiguredModels(configForm);
  const seen = new Set(configOptions.map((o) => o.value));

  // Add all catalog models (the catalog already only contains models from
  // configured/connected providers — no extra filtering needed).
  if (catalog && catalog.length > 0) {
    for (const entry of catalog) {
      const key = `${entry.provider}/${entry.id}`;
      if (seen.has(key)) {
        continue;
      }
      // Skip embedding models
      if (entry.id.includes("embedding") || entry.id.includes("embed-text")) {
        continue;
      }
      // Skip TTS models
      if (entry.id.includes("-tts-")) {
        continue;
      }
      seen.add(key);
      configOptions.push({ value: key, label: key });
    }
  }

  const hasCurrent = current ? configOptions.some((option) => option.value === current) : false;
  if (current && !hasCurrent) {
    configOptions.unshift({ value: current, label: `Current (${current})` });
  }
  if (configOptions.length === 0) {
    return html`
      <option value="" disabled>No configured models</option>
    `;
  }
  return configOptions.map(
    (option) =>
      html`<option value=${option.value} ?selected=${current === option.value}>${option.label}</option>`,
  );
}

function buildFallbackOptions(
  configForm: Record<string, unknown> | null,
  primary: string | null | undefined,
  currentFallbacks: string[] | null,
  catalog?: CatalogModelEntry[],
) {
  const configOptions = resolveConfiguredModels(configForm);
  const seen = new Set(configOptions.map((o) => o.value));

  if (catalog && catalog.length > 0) {
    for (const entry of catalog) {
      const key = `${entry.provider}/${entry.id}`;
      if (seen.has(key)) {
        continue;
      }
      if (entry.id.includes("embedding") || entry.id.includes("embed-text")) {
        continue;
      }
      if (entry.id.includes("-tts-")) {
        continue;
      }
      seen.add(key);
      configOptions.push({ value: key, label: key });
    }
  }

  // Exclude the primary model and already-selected fallbacks
  const excludeSet = new Set<string>();
  if (primary) excludeSet.add(primary);
  if (currentFallbacks) {
    for (const fb of currentFallbacks) excludeSet.add(fb);
  }

  const filtered = configOptions.filter((o) => !excludeSet.has(o.value));
  if (filtered.length === 0) {
    return html`
      <option value="" disabled>No more models available</option>
    `;
  }
  return filtered.map((option) => html`<option value=${option.value}>${option.label}</option>`);
}

type CompiledPattern =
  | { kind: "all" }
  | { kind: "exact"; value: string }
  | { kind: "regex"; value: RegExp };

function compilePattern(pattern: string): CompiledPattern {
  const normalized = normalizeToolName(pattern);
  if (!normalized) {
    return { kind: "exact", value: "" };
  }
  if (normalized === "*") {
    return { kind: "all" };
  }
  if (!normalized.includes("*")) {
    return { kind: "exact", value: normalized };
  }
  const escaped = normalized.replace(/[.*+?^${}()|[\\]\\]/g, "\\$&");
  return { kind: "regex", value: new RegExp(`^${escaped.replaceAll("\\*", ".*")}$`) };
}

function compilePatterns(patterns?: string[]): CompiledPattern[] {
  if (!Array.isArray(patterns)) {
    return [];
  }
  return expandToolGroups(patterns)
    .map(compilePattern)
    .filter((pattern) => {
      return pattern.kind !== "exact" || pattern.value.length > 0;
    });
}

function matchesAny(name: string, patterns: CompiledPattern[]) {
  for (const pattern of patterns) {
    if (pattern.kind === "all") {
      return true;
    }
    if (pattern.kind === "exact" && name === pattern.value) {
      return true;
    }
    if (pattern.kind === "regex" && pattern.value.test(name)) {
      return true;
    }
  }
  return false;
}

function isAllowedByPolicy(name: string, policy?: ToolPolicy) {
  if (!policy) {
    return true;
  }
  const normalized = normalizeToolName(name);
  const deny = compilePatterns(policy.deny);
  if (matchesAny(normalized, deny)) {
    return false;
  }
  const allow = compilePatterns(policy.allow);
  if (allow.length === 0) {
    return true;
  }
  if (matchesAny(normalized, allow)) {
    return true;
  }
  if (normalized === "apply_patch" && matchesAny("exec", allow)) {
    return true;
  }
  return false;
}

function matchesList(name: string, list?: string[]) {
  if (!Array.isArray(list) || list.length === 0) {
    return false;
  }
  const normalized = normalizeToolName(name);
  const patterns = compilePatterns(list);
  if (matchesAny(normalized, patterns)) {
    return true;
  }
  if (normalized === "apply_patch" && matchesAny("exec", patterns)) {
    return true;
  }
  return false;
}

export function renderAgents(props: AgentsProps) {
  const agents = props.agentsList?.agents ?? [];
  const defaultId = props.agentsList?.defaultId ?? null;
  const selectedId = props.selectedAgentId ?? defaultId ?? agents[0]?.id ?? null;
  const agentFromList = selectedId
    ? (agents.find((agent) => agent.id === selectedId) ?? null)
    : null;
  // If the selected agent isn't in agents.list (e.g. dynamic agent not yet
  // visible through the provider), synthesize a row from agentDefs.
  const selectedAgent: typeof agentFromList =
    agentFromList ??
    (() => {
      if (!selectedId) {
        return null;
      }
      const def = props.agentDefs.find((d) => d.agentId === selectedId);
      if (!def) {
        return null;
      }
      return {
        id: def.agentId,
        name: def.name,
        identity: { name: def.name, theme: def.description },
      };
    })();

  // Split agents into static vs dynamic based on agentDefs.
  // Dynamic agents are rendered directly from agentDefs (DB source of truth)
  // rather than from the agents.list intersection, because the agents.list
  // provider may not include them if the gateway hasn't been restarted.
  const dynamicIds = new Set(props.agentDefs.map((d) => d.agentId));
  const staticAgents = agents.filter((a) => !dynamicIds.has(a.id));
  const isEditing = props.agentDefCreateMode || props.agentDefEditingId != null;

  return html`
    <div class="agents-layout">
      <section class="card agents-sidebar">
        <div class="row" style="justify-content: space-between;">
          <div>
            <div class="card-title">Agents</div>
            <div class="card-sub">${staticAgents.length} static, ${props.agentDefs.length} dynamic.</div>
          </div>
          <button class="btn btn--sm" ?disabled=${props.loading} @click=${props.onRefresh}>
            ${props.loading ? "Loading…" : "Refresh"}
          </button>
        </div>
        ${
          props.error
            ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
            : nothing
        }

        <!-- Static Agents -->
        <div class="agent-list" style="margin-top: 12px;">
          ${
            staticAgents.length === 0
              ? html`
                  <div class="muted">No static agents found.</div>
                `
              : staticAgents.map((agent) => {
                  const badge = agentBadgeText(agent.id, defaultId);
                  const emoji = resolveAgentEmoji(agent, props.agentIdentityById[agent.id] ?? null);
                  return html`
                    <button
                      type="button"
                      class="agent-row ${selectedId === agent.id ? "active" : ""}"
                      @click=${() => props.onSelectAgent(agent.id)}
                    >
                      <div class="agent-avatar">
                        ${emoji || normalizeAgentLabel(agent).slice(0, 1)}
                      </div>
                      <div class="agent-info">
                        <div class="agent-title">${normalizeAgentLabel(agent)}</div>
                        <div class="agent-sub mono">${agent.id}</div>
                      </div>
                      ${badge ? html`<span class="agent-pill">${badge}</span>` : nothing}
                    </button>
                  `;
                })
          }
        </div>

        <!-- + New Agent button -->
        <div style="margin-top:12px;">
          <button
            class="btn primary"
            style="width:100%;"
            @click=${props.onAgentDefCreate}
            ?disabled=${isEditing}
          >
            + New Agent
          </button>
        </div>

        <!-- Dynamic Agents -->
        <div style="margin-top:12px;padding-top:12px;border-top:1px solid var(--glass-border);">
          <div class="row" style="justify-content:space-between;margin-bottom:8px;">
            <div>
              <div style="font-weight:600;font-size:0.9em;">Dynamic Agents</div>
              <div class="muted" style="font-size:0.8em;">${props.agentDefs.length} DB-backed</div>
            </div>
            <button
              class="btn btn--sm"
              ?disabled=${props.agentDefsLoading}
              @click=${props.onAgentDefsRefresh}
            >
              ${props.agentDefsLoading ? "..." : "Refresh"}
            </button>
          </div>
          ${
            props.agentDefsError
              ? html`<div class="callout danger" style="font-size:0.85em;">${props.agentDefsError}</div>`
              : nothing
          }
          <div class="agent-list">
            ${
              props.agentDefs.length === 0
                ? html`
                    <div class="muted" style="font-size: 0.85em">No dynamic agents yet.</div>
                  `
                : props.agentDefs.map((def) => {
                    const agentRow = agents.find((a) => a.id === def.agentId);
                    const emoji = agentRow
                      ? resolveAgentEmoji(agentRow, props.agentIdentityById[def.agentId] ?? null)
                      : null;
                    return html`
                      <button
                        type="button"
                        class="agent-row ${selectedId === def.agentId ? "active" : ""}"
                        @click=${() => props.onSelectAgent(def.agentId)}
                      >
                        <div class="agent-avatar">
                          ${emoji || def.name.slice(0, 1)}
                        </div>
                        <div class="agent-info">
                          <div class="agent-title">${def.name}</div>
                          <div class="agent-sub mono">${def.agentId}</div>
                        </div>
                        <span class="agent-pill" style="${def.isActive ? "" : "opacity:0.5;"}">
                          ${def.isActive ? "dynamic" : "inactive"}
                        </span>
                      </button>
                    `;
                  })
            }
          </div>
        </div>
      </section>
      <section class="agents-main">
        ${
          isEditing && props.agentDefForm
            ? html`
                <div class="card">
                  <div class="card-title">
                    ${props.agentDefEditingId ? "Edit Dynamic Agent" : "Create Dynamic Agent"}
                  </div>
                  <div class="card-sub">
                    ${
                      props.agentDefEditingId
                        ? `Editing definition for "${props.agentDefEditingId}".`
                        : "Define a new DB-backed agent."
                    }
                  </div>
                </div>
                ${renderAgentDefForm(props)}
              `
            : !selectedAgent
              ? html`
                  <div class="card">
                    <div class="card-title">Select an agent</div>
                    <div class="card-sub">Pick an agent to inspect its workspace and tools.</div>
                  </div>
                `
              : html`
                ${renderAgentHeader(
                  selectedAgent,
                  defaultId,
                  props.agentIdentityById[selectedAgent.id] ?? null,
                )}
                ${
                  selectedAgent && dynamicIds.has(selectedAgent.id)
                    ? renderDynamicAgentActions(props, selectedAgent.id)
                    : nothing
                }
                ${renderAgentTabs(props.activePanel, (panel) => props.onSelectPanel(panel))}
              ${
                props.activePanel === "overview"
                  ? renderAgentOverview({
                      agent: selectedAgent,
                      defaultId,
                      configForm: props.configForm,
                      agentFilesList: props.agentFilesList,
                      agentIdentity: props.agentIdentityById[selectedAgent.id] ?? null,
                      agentIdentityError: props.agentIdentityError,
                      agentIdentityLoading: props.agentIdentityLoading,
                      configLoading: props.configLoading,
                      configSaving: props.configSaving,
                      configDirty: props.configDirty,
                      modelCatalog: props.modelCatalog,
                      onConfigReload: props.onConfigReload,
                      onConfigSave: props.onConfigSave,
                      onModelChange: props.onModelChange,
                      onModelFallbacksChange: props.onModelFallbacksChange,
                      agentDef: props.agentDefs.find((d) => d.agentId === selectedAgent.id) ?? null,
                    })
                  : nothing
              }
              ${
                props.activePanel === "files"
                  ? renderAgentFiles({
                      agentId: selectedAgent.id,
                      agentFilesList: props.agentFilesList,
                      agentFilesLoading: props.agentFilesLoading,
                      agentFilesError: props.agentFilesError,
                      agentFileActive: props.agentFileActive,
                      agentFileContents: props.agentFileContents,
                      agentFileDrafts: props.agentFileDrafts,
                      agentFileSaving: props.agentFileSaving,
                      onLoadFiles: props.onLoadFiles,
                      onSelectFile: props.onSelectFile,
                      onFileDraftChange: props.onFileDraftChange,
                      onFileReset: props.onFileReset,
                      onFileSave: props.onFileSave,
                    })
                  : nothing
              }
              ${
                props.activePanel === "tools"
                  ? renderAgentTools({
                      agentId: selectedAgent.id,
                      configForm: props.configForm,
                      configLoading: props.configLoading,
                      configSaving: props.configSaving,
                      configDirty: props.configDirty,
                      onProfileChange: props.onToolsProfileChange,
                      onOverridesChange: props.onToolsOverridesChange,
                      onConfigReload: props.onConfigReload,
                      onConfigSave: props.onConfigSave,
                      agentDef: props.agentDefs.find((d) => d.agentId === selectedAgent.id) ?? null,
                    })
                  : nothing
              }
              ${
                props.activePanel === "skills"
                  ? renderAgentSkills({
                      agentId: selectedAgent.id,
                      report: props.agentSkillsReport,
                      loading: props.agentSkillsLoading,
                      error: props.agentSkillsError,
                      activeAgentId: props.agentSkillsAgentId,
                      configForm: props.configForm,
                      configLoading: props.configLoading,
                      configSaving: props.configSaving,
                      configDirty: props.configDirty,
                      filter: props.skillsFilter,
                      onFilterChange: props.onSkillsFilterChange,
                      onRefresh: props.onSkillsRefresh,
                      onToggle: props.onAgentSkillToggle,
                      onClear: props.onAgentSkillsClear,
                      onDisableAll: props.onAgentSkillsDisableAll,
                      onConfigReload: props.onConfigReload,
                      onConfigSave: props.onConfigSave,
                      agentDef: props.agentDefs.find((d) => d.agentId === selectedAgent.id) ?? null,
                    })
                  : nothing
              }
              ${
                props.activePanel === "tasks"
                  ? renderAgentTasks({
                      tasks: props.agentTasks,
                      loading: props.agentTasksLoading,
                      error: props.agentTasksError,
                      onRefresh: props.onAgentTasksRefresh,
                    })
                  : nothing
              }
              ${
                props.activePanel === "channels"
                  ? renderAgentChannels({
                      agent: selectedAgent,
                      defaultId,
                      configForm: props.configForm,
                      agentFilesList: props.agentFilesList,
                      agentIdentity: props.agentIdentityById[selectedAgent.id] ?? null,
                      snapshot: props.channelsSnapshot,
                      loading: props.channelsLoading,
                      error: props.channelsError,
                      lastSuccess: props.channelsLastSuccess,
                      onRefresh: props.onChannelsRefresh,
                    })
                  : nothing
              }
              ${
                props.activePanel === "cron"
                  ? renderAgentCron({
                      agent: selectedAgent,
                      defaultId,
                      configForm: props.configForm,
                      agentFilesList: props.agentFilesList,
                      agentIdentity: props.agentIdentityById[selectedAgent.id] ?? null,
                      jobs: props.cronJobs,
                      status: props.cronStatus,
                      loading: props.cronLoading,
                      error: props.cronError,
                      projectCronJobs: props.agentProjectCronJobs,
                      projectCronJobsLoading: props.agentProjectCronJobsLoading,
                      onRefresh: props.onCronRefresh,
                    })
                  : nothing
              }
            `
        }
      </section>
    </div>
  `;
}

// --------------- Agent Tasks Sub-tab ---------------

const TASK_STATUS_COLORS: Record<string, string> = {
  pending: "",
  scheduled: "",
  contacted: "pill--ok",
  awaiting_response: "pill--ok",
  in_progress: "pill--ok",
  completed: "pill--ok",
  failed: "pill--danger",
  escalated: "pill--danger",
  cancelled: "",
};

function renderAgentTasks(opts: {
  tasks: TaskEntry[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  const { tasks, loading, error, onRefresh } = opts;

  // Status counts
  const statusCounts = new Map<string, number>();
  for (const task of tasks) {
    statusCounts.set(task.status, (statusCounts.get(task.status) ?? 0) + 1);
  }

  return html`
    <section class="card" style="margin-top:16px;">
      <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:12px;">
        <div>
          <div class="card-title">Tasks</div>
          <div class="card-sub">
            Outbound tasks assigned to projects managed by this agent.
          </div>
        </div>
        <button class="btn" @click=${onRefresh} ?disabled=${loading}>
          ${loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      ${error ? html`<div class="callout danger" style="margin-top:12px;">${error}</div>` : nothing}

      <!-- Stats row -->
      <div style="display:flex;gap:16px;margin-top:12px;flex-wrap:wrap;">
        <div class="stat">
          <div class="stat-value">${tasks.length}</div>
          <div class="stat-label">Total</div>
        </div>
        <div class="stat">
          <div class="stat-value">${statusCounts.get("in_progress") ?? 0}</div>
          <div class="stat-label">In Progress</div>
        </div>
        <div class="stat">
          <div class="stat-value">${statusCounts.get("awaiting_response") ?? 0}</div>
          <div class="stat-label">Awaiting</div>
        </div>
        <div class="stat">
          <div class="stat-value">${statusCounts.get("completed") ?? 0}</div>
          <div class="stat-label">Completed</div>
        </div>
        <div class="stat">
          <div class="stat-value">${(statusCounts.get("failed") ?? 0) + (statusCounts.get("escalated") ?? 0)}</div>
          <div class="stat-label">Failed</div>
        </div>
      </div>

      ${
        tasks.length === 0 && !loading
          ? html`
              <div class="callout info" style="margin-top: 12px">No tasks found for this agent's projects.</div>
            `
          : nothing
      }

      ${
        tasks.length > 0
          ? html`
              <div class="table" style="margin-top:16px;">
                <div class="table-head">
                  <span style="flex:2">Title</span>
                  <span style="flex:1">Type</span>
                  <span style="flex:1">Status</span>
                  <span style="flex:1">Project</span>
                  <span style="flex:1">Assigned To</span>
                  <span style="flex:1">Created</span>
                </div>
                ${tasks.map(
                  (task) => html`
                    <div class="table-row">
                      <span style="flex:2"><strong>${task.title}</strong></span>
                      <span style="flex:1">
                        <span class="pill pill--sm">${task.type.replace(/_/g, " ")}</span>
                      </span>
                      <span style="flex:1">
                        <span class="pill pill--sm ${TASK_STATUS_COLORS[task.status] ?? ""}">
                          ${task.status.replace(/_/g, " ")}
                        </span>
                      </span>
                      <span style="flex:1">${task.projectName ?? task.projectId}</span>
                      <span style="flex:1">${task.assignedUserName ?? "Unassigned"}</span>
                      <span style="flex:1" class="muted">${task.createdAt.split("T")[0]}</span>
                    </div>
                  `,
                )}
              </div>
            `
          : nothing
      }
    </section>
  `;
}

// --------------- Dynamic Agent Actions (header bar for selected dynamic agent) ---------------

function renderDynamicAgentActions(props: AgentsProps, agentId: string) {
  const def = props.agentDefs.find((d) => d.agentId === agentId);
  if (!def) {
    return nothing;
  }

  return html`
    <div class="card" style="padding:10px 16px;display:flex;align-items:center;justify-content:space-between;gap:12px;">
      <div style="display:flex;align-items:center;gap:8px;">
        <span class="chip ${def.isActive ? "chip-ok" : "chip-warn"}">
          ${def.isActive ? "Active" : "Inactive"}
        </span>
        <span class="muted" style="font-size:0.85em;">Dynamic Agent</span>
        ${
          def.projectName
            ? html`<span class="muted" style="font-size:0.85em;">&middot; ${def.projectName}</span>`
            : nothing
        }
      </div>
      <div style="display:flex;gap:6px;">
        <button
          class="btn btn--sm"
          @click=${() => props.onAgentDefToggle(def.agentId, !def.isActive)}
        >
          ${def.isActive ? "Disable" : "Enable"}
        </button>
        <button class="btn btn--sm" @click=${() => props.onAgentDefEdit(def)}>
          Edit Definition
        </button>
        <button
          class="btn btn--sm danger"
          @click=${() => props.onAgentDefDelete(def.agentId)}
        >
          Delete
        </button>
      </div>
    </div>
  `;
}

function renderAgentDefForm(props: AgentsProps) {
  const form = props.agentDefForm!;
  const isEdit = props.agentDefEditingId != null;
  return html`
    <div style="margin-top:16px;padding:16px;border:1px solid var(--border);border-radius:8px;">
      <div style="font-weight:600;margin-bottom:12px;">
        ${isEdit ? "Edit Agent Definition" : "Create Agent Definition"}
      </div>
      <div class="form-grid">
        <label class="field">
          <span>Name</span>
          <input
            .value=${String(form.name ?? "")}
            @input=${(e: Event) =>
              props.onAgentDefFormChange("name", (e.target as HTMLInputElement).value)}
            placeholder="Agent display name"
          />
        </label>
        <label class="field">
          <span>Project</span>
          <select
            .value=${String(form.projectId ?? "")}
            @change=${(e: Event) =>
              props.onAgentDefFormChange("projectId", (e.target as HTMLSelectElement).value)}
          >
            <option value="">No project (global)</option>
            ${props.projects.map(
              (p) =>
                html`<option value=${p.id} ?selected=${p.id === form.projectId}>${p.name}</option>`,
            )}
          </select>
        </label>
        <label class="field">
          <span>Primary Model</span>
          <select
            .value=${String(
              form.model && typeof form.model === "object"
                ? ((form.model as { primary?: string }).primary ?? "")
                : "",
            )}
            @change=${(e: Event) => {
              const val = (e.target as HTMLSelectElement).value;
              const existing =
                form.model && typeof form.model === "object"
                  ? (form.model as { fallbacks?: string[] }).fallbacks
                  : undefined;
              props.onAgentDefFormChange(
                "model",
                val
                  ? {
                      primary: val,
                      ...(Array.isArray(existing) && existing.length > 0
                        ? { fallbacks: existing }
                        : {}),
                    }
                  : null,
              );
            }}
          >
            <option value="">Select a model…</option>
            ${buildModelOptions(
              props.configForm,
              form.model && typeof form.model === "object"
                ? ((form.model as { primary?: string }).primary ?? undefined)
                : undefined,
              props.modelCatalog,
            )}
          </select>
        </label>
        <label class="field">
          <span>Fallback Models</span>
          <select
            @change=${(e: Event) => {
              const sel = e.target as HTMLSelectElement;
              const val = sel.value;
              if (!val) {
                return;
              }
              sel.value = "";
              const existing =
                form.model && typeof form.model === "object"
                  ? (form.model as { primary?: string; fallbacks?: string[] })
                  : null;
              const primary = existing?.primary ?? "";
              const currentFb = Array.isArray(existing?.fallbacks) ? existing.fallbacks : [];
              if (!currentFb.includes(val)) {
                props.onAgentDefFormChange("model", {
                  ...(primary ? { primary } : {}),
                  fallbacks: [...currentFb, val],
                });
              }
            }}
          >
            <option value="">Add fallback model…</option>
            ${buildFallbackOptions(
              props.configForm,
              form.model && typeof form.model === "object"
                ? ((form.model as { primary?: string }).primary ?? null)
                : null,
              form.model && typeof form.model === "object"
                ? ((form.model as { fallbacks?: string[] }).fallbacks ?? null)
                : null,
              props.modelCatalog,
            )}
          </select>
          ${(() => {
            const fb =
              form.model && typeof form.model === "object"
                ? (form.model as { fallbacks?: string[] }).fallbacks
                : null;
            if (!fb || fb.length === 0) {
              return nothing;
            }
            return html`
              <div style="display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px;">
                ${fb.map(
                  (f, idx) => html`
                    <span style="display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 4px; background: var(--surface-2, #e8e8e8); font-size: 0.85em;">
                      <span style="opacity: 0.5; font-size: 0.8em; margin-right: 2px;">${idx + 1}.</span>
                      ${f}
                      <button
                        type="button"
                        style="background: none; border: none; cursor: pointer; padding: 0 2px; font-size: 1.1em; line-height: 1; opacity: 0.6; color: inherit;"
                        title="Remove fallback"
                        @click=${() => {
                          const existing =
                            form.model && typeof form.model === "object"
                              ? (form.model as { primary?: string; fallbacks?: string[] })
                              : null;
                          const updated = (fb ?? []).filter((_, i) => i !== idx);
                          props.onAgentDefFormChange("model", {
                            ...(existing?.primary ? { primary: existing.primary } : {}),
                            ...(updated.length > 0 ? { fallbacks: updated } : {}),
                          });
                        }}
                      >x</button>
                    </span>
                  `,
                )}
              </div>
            `;
          })()}
        </label>
        <label class="field full">
          <span>Description</span>
          <input
            .value=${String(form.description ?? "")}
            @input=${(e: Event) =>
              props.onAgentDefFormChange("description", (e.target as HTMLInputElement).value)}
            placeholder="Brief description"
          />
        </label>
        <label class="field full">
          <span>System Prompt Override</span>
          <textarea
            rows="4"
            .value=${String(form.systemPromptOverride ?? "")}
            @input=${(e: Event) =>
              props.onAgentDefFormChange(
                "systemPromptOverride",
                (e.target as HTMLTextAreaElement).value,
              )}
            placeholder="Optional system prompt override (leave blank to use project default)"
          ></textarea>
        </label>
      </div>

      ${renderAgentDefBindings(props)}

      <div style="display:flex;gap:8px;margin-top:16px;">
        <button class="btn primary" @click=${props.onAgentDefSave} ?disabled=${props.agentDefsLoading}>
          ${isEdit ? "Update" : "Create"}
        </button>
        <button class="btn" @click=${props.onAgentDefCancel}>Cancel</button>
      </div>

      ${isEdit ? renderAgentDefIdentityFiles(props) : nothing}
    </div>
  `;
}

function renderAgentDefBindings(props: AgentsProps) {
  const form = props.agentDefForm;
  if (!form) {
    return nothing;
  }
  const bindings = form.bindings ?? [];

  return html`
    <div style="margin-top:16px;border-top:1px solid var(--border);padding-top:12px;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
        <div>
          <div style="font-weight:600;font-size:0.95em;">Channel Bindings</div>
          <div class="muted" style="font-size:0.82em;">Route inbound messages from specific channels to this agent.</div>
        </div>
        <button class="btn btn--sm" @click=${props.onAgentDefBindingAdd}>+ Add Binding</button>
      </div>

      ${
        bindings.length === 0
          ? html`
              <div class="muted" style="font-size: 0.85em; padding: 8px 0">
                No bindings configured. Messages will only reach this agent via project assignment or direct
                session creation.
              </div>
            `
          : nothing
      }

      ${bindings.map(
        (binding, index) => html`
          <div style="display:flex;gap:8px;align-items:flex-end;margin-bottom:8px;padding:10px;border:1px solid var(--border);border-radius:6px;background:var(--bg-card-nested, var(--bg-input));">
            <label class="field" style="flex:1;margin:0;">
              <span style="font-size:0.82em;">Channel</span>
              <select
                .value=${binding.channel ?? ""}
                @change=${(e: Event) =>
                  props.onAgentDefBindingChange(
                    index,
                    "channel",
                    (e.target as HTMLSelectElement).value,
                  )}
              >
                <option value="" disabled ?selected=${!binding.channel}>Select channel</option>
                <option value="telegram" ?selected=${binding.channel === "telegram"}>Telegram</option>
                <option value="whatsapp" ?selected=${binding.channel === "whatsapp"}>WhatsApp</option>
                <option value="email" ?selected=${binding.channel === "email"}>Email</option>
                <option value="webchat" ?selected=${binding.channel === "webchat"}>Webchat</option>
              </select>
            </label>
            <label class="field" style="flex:1;margin:0;">
              <span style="font-size:0.82em;">Account ID</span>
              <input
                .value=${binding.accountId ?? ""}
                @input=${(e: Event) =>
                  props.onAgentDefBindingChange(
                    index,
                    "accountId",
                    (e.target as HTMLInputElement).value,
                  )}
                placeholder="* (any) or specific ID"
              />
            </label>
            <label class="field" style="flex:0.8;margin:0;">
              <span style="font-size:0.82em;">Guild/Team ID</span>
              <input
                .value=${binding.guildId ?? binding.teamId ?? ""}
                @input=${(e: Event) => {
                  const val = (e.target as HTMLInputElement).value;
                  const ch = binding.channel ?? "";
                  if (ch === "discord") {
                    props.onAgentDefBindingChange(index, "guildId", val);
                  } else if (ch === "slack") {
                    props.onAgentDefBindingChange(index, "teamId", val);
                  } else {
                    props.onAgentDefBindingChange(index, "guildId", val);
                  }
                }}
                placeholder="Optional"
              />
            </label>
            <button
              class="btn btn--sm danger"
              style="margin-bottom:2px;"
              @click=${() => props.onAgentDefBindingRemove(index)}
              title="Remove binding"
            >
              X
            </button>
          </div>
        `,
      )}
    </div>
  `;
}

function renderAgentDefIdentityFiles(props: AgentsProps) {
  const agentId = props.agentDefEditingId;
  if (!agentId) {
    return nothing;
  }

  const files = props.agentDefIdFiles;
  const activeFile = props.agentDefIdFileActive;
  const activeInfo = activeFile ? files.find((f) => f.fileName === activeFile) : null;
  const draft = activeFile ? (props.agentDefIdFileDrafts[activeFile] ?? "") : "";
  const baseContent = activeInfo?.content ?? "";
  const isDirty = activeFile ? draft !== baseContent : false;

  return html`
    <div style="margin-top:24px;border-top:1px solid var(--border);padding-top:16px;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <div>
          <div style="font-weight:600;">Identity File Overrides</div>
          <div class="muted" style="font-size:0.85em;">
            Customize identity files for this agent. Files without overrides use the default template.
          </div>
        </div>
        ${
          props.agentDefIdFilesLoading
            ? html`
                <span class="muted">Loading...</span>
              `
            : nothing
        }
      </div>

      <div style="display:grid;grid-template-columns:200px 1fr;gap:16px;min-height:300px;">
        <!-- File list -->
        <div style="display:flex;flex-direction:column;gap:2px;">
          ${files.map(
            (f) => html`
              <button
                class="btn btn--sm ${f.fileName === activeFile ? "primary" : ""}"
                style="text-align:left;justify-content:flex-start;gap:8px;font-family:var(--font-mono);font-size:0.85em;"
                @click=${() => props.onAgentDefIdFileSelect(f.fileName)}
              >
                ${
                  f.hasOverride
                    ? html`
                        <span title="Has custom override" style="color: var(--accent)">&#9670;</span>
                      `
                    : html`
                        <span style="opacity: 0.3">&#9671;</span>
                      `
                }
                ${f.fileName}
              </button>
            `,
          )}
          ${
            files.length === 0 && !props.agentDefIdFilesLoading
              ? html`
                  <div class="muted" style="padding: 8px; font-size: 0.85em">No files available.</div>
                `
              : nothing
          }
        </div>

        <!-- Editor -->
        <div style="display:flex;flex-direction:column;gap:8px;">
          ${
            activeFile && activeInfo
              ? html`
                <div style="display:flex;justify-content:space-between;align-items:center;">
                  <div>
                    <span class="mono" style="font-weight:600;">${activeFile}</span>
                    <span class="chip ${activeInfo.hasOverride ? "chip-ok" : "chip-info"}" style="margin-left:8px;">
                      ${activeInfo.hasOverride ? "Custom Override" : "Default Template"}
                    </span>
                  </div>
                  <div style="display:flex;gap:6px;">
                    ${
                      activeInfo.hasOverride
                        ? html`
                          <button
                            class="btn btn--sm"
                            @click=${() => props.onAgentDefIdFileReset(agentId, activeFile)}
                            ?disabled=${props.agentDefIdFileSaving}
                          >
                            Reset to Default
                          </button>
                        `
                        : nothing
                    }
                    <button
                      class="btn btn--sm primary"
                      @click=${() => props.onAgentDefIdFileSave(agentId, activeFile)}
                      ?disabled=${!isDirty || props.agentDefIdFileSaving}
                    >
                      ${props.agentDefIdFileSaving ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
                <textarea
                  style="width:100%;min-height:280px;font-family:var(--font-mono);font-size:0.85em;padding:8px;border:1px solid var(--border);border-radius:6px;resize:vertical;background:var(--bg-input);color:var(--fg);"
                  .value=${draft}
                  @input=${(e: Event) =>
                    props.onAgentDefIdFileDraftChange(
                      activeFile,
                      (e.target as HTMLTextAreaElement).value,
                    )}
                ></textarea>
              `
              : html`
                  <div class="muted" style="padding: 24px; text-align: center">
                    Select a file from the list to view or edit.
                  </div>
                `
          }
        </div>
      </div>
    </div>
  `;
}

// --------------- Agent Header ---------------

function renderAgentHeader(
  agent: AgentsListResult["agents"][number],
  defaultId: string | null,
  agentIdentity: AgentIdentityResult | null,
) {
  const badge = agentBadgeText(agent.id, defaultId);
  const displayName = normalizeAgentLabel(agent);
  const subtitle =
    agent.identity?.theme?.trim() ||
    (agent.id === "main" ? "helper" : "Agent workspace and routing.");
  const emoji = resolveAgentEmoji(agent, agentIdentity);
  return html`
    <section class="card agent-header">
      <div class="agent-header-main">
        <div class="agent-avatar agent-avatar--lg">
          ${emoji || displayName.slice(0, 1)}
        </div>
        <div>
          <div class="card-title">${displayName}</div>
          <div class="card-sub">${subtitle}</div>
        </div>
      </div>
      <div class="agent-header-meta">
        <div class="mono">${agent.id}</div>
        ${badge ? html`<span class="agent-pill">${badge}</span>` : nothing}
      </div>
    </section>
  `;
}

function renderAgentTabs(active: AgentsPanel, onSelect: (panel: AgentsPanel) => void) {
  const tabs: Array<{ id: AgentsPanel; label: string }> = [
    { id: "overview", label: "Overview" },
    { id: "files", label: "Files" },
    { id: "tools", label: "Tools" },
    { id: "skills", label: "Skills" },
    { id: "tasks", label: "Tasks" },
    { id: "channels", label: "Channels" },
    { id: "cron", label: "Cron Jobs" },
  ];
  return html`
    <div class="agent-tabs">
      ${tabs.map(
        (tab) => html`
          <button
            class="agent-tab ${active === tab.id ? "active" : ""}"
            type="button"
            @click=${() => onSelect(tab.id)}
          >
            ${tab.label}
          </button>
        `,
      )}
    </div>
  `;
}

function renderAgentOverview(params: {
  agent: AgentsListResult["agents"][number];
  defaultId: string | null;
  configForm: Record<string, unknown> | null;
  agentFilesList: AgentsFilesListResult | null;
  agentIdentity: AgentIdentityResult | null;
  agentIdentityLoading: boolean;
  agentIdentityError: string | null;
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  modelCatalog: CatalogModelEntry[];
  onConfigReload: () => void;
  onConfigSave: () => void;
  onModelChange: (agentId: string, modelId: string | null) => void;
  onModelFallbacksChange: (agentId: string, fallbacks: string[]) => void;
  agentDef: AgentDefinitionEntry | null;
}) {
  const {
    agent,
    configForm,
    agentFilesList,
    agentIdentity,
    agentIdentityLoading,
    agentIdentityError,
    configLoading,
    configSaving,
    configDirty,
    onConfigReload,
    onConfigSave,
    onModelChange,
    onModelFallbacksChange,
  } = params;
  const config = resolveAgentConfig(configForm, agent.id);
  const isDynamic = Boolean(params.agentDef);
  const workspaceFromFiles =
    agentFilesList && agentFilesList.agentId === agent.id ? agentFilesList.workspace : null;
  const workspace =
    workspaceFromFiles || config.entry?.workspace || config.defaults?.workspace || "default";
  // For dynamic agents, read model from the DB definition (jsonb: { primary?, fallbacks? })
  const dynamicModelObj =
    isDynamic && params.agentDef?.model && typeof params.agentDef.model === "object"
      ? (params.agentDef.model as { primary?: string; fallbacks?: string[] })
      : null;
  const dynamicPrimary = dynamicModelObj?.primary || null;
  const model = dynamicPrimary
    ? dynamicPrimary
    : config.entry?.model
      ? resolveModelLabel(config.entry?.model)
      : resolveModelLabel(config.defaults?.model);
  const defaultModel = resolveModelLabel(config.defaults?.model);
  const modelPrimary = dynamicPrimary
    ? normalizeModelValue(dynamicPrimary)
    : resolveModelPrimary(config.entry?.model) ||
      (model !== "-" ? normalizeModelValue(model) : null);
  const defaultPrimary =
    resolveModelPrimary(config.defaults?.model) ||
    (defaultModel !== "-" ? normalizeModelValue(defaultModel) : null);
  const effectivePrimary = modelPrimary ?? defaultPrimary ?? null;
  const modelFallbacks = isDynamic
    ? Array.isArray(dynamicModelObj?.fallbacks)
      ? dynamicModelObj!.fallbacks
      : null
    : resolveModelFallbacks(config.entry?.model);
  const identityName =
    agentIdentity?.name?.trim() ||
    agent.identity?.name?.trim() ||
    agent.name?.trim() ||
    config.entry?.name ||
    "-";
  const resolvedEmoji = resolveAgentEmoji(agent, agentIdentity);
  const identityEmoji = resolvedEmoji || "-";
  // For dynamic agents, read skills from the DB definition
  const dynamicSkills =
    isDynamic && Array.isArray(params.agentDef?.skills) && params.agentDef!.skills.length > 0
      ? params.agentDef!.skills
      : null;
  const skillFilter =
    dynamicSkills ?? (Array.isArray(config.entry?.skills) ? config.entry?.skills : null);
  const skillCount = skillFilter?.length ?? null;
  const identityStatus = agentIdentityLoading
    ? "Loading…"
    : agentIdentityError
      ? "Unavailable"
      : "";
  const isDefault = Boolean(params.defaultId && agent.id === params.defaultId);

  return html`
    <section class="card">
      <div class="card-title">Overview</div>
      <div class="card-sub">Workspace paths and identity metadata.</div>
      <div class="agents-overview-grid" style="margin-top: 16px;">
        <div class="agent-kv">
          <div class="label">Workspace</div>
          <div class="mono">${workspace}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Primary Model</div>
          <div class="mono">${model}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Identity Name</div>
          <div>${identityName}</div>
          ${identityStatus ? html`<div class="agent-kv-sub muted">${identityStatus}</div>` : nothing}
        </div>
        <div class="agent-kv">
          <div class="label">Default</div>
          <div>${isDefault ? "yes" : "no"}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Identity Emoji</div>
          <div>${identityEmoji}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Skills Filter</div>
          <div>${skillFilter ? `${skillCount} selected` : "all skills"}</div>
        </div>
      </div>

      <div class="agent-model-select" style="margin-top: 20px;">
        <div class="label">Model Selection</div>
        <div class="row" style="gap: 12px; flex-wrap: wrap;">
          <label class="field" style="min-width: 260px; flex: 1;">
            <span>Primary model${isDefault ? " (default)" : ""}</span>
            <select
              .value=${effectivePrimary ?? ""}
              ?disabled=${(!configForm && !isDynamic) || configLoading || configSaving}
              @change=${(e: Event) =>
                onModelChange(agent.id, (e.target as HTMLSelectElement).value || null)}
            >
              ${
                isDefault
                  ? nothing
                  : html`
                      <option value="" ?selected=${!modelPrimary}>
                        ${
                          defaultPrimary ? `Inherit default (${defaultPrimary})` : "Inherit default"
                        }
                      </option>
                    `
              }
              ${buildModelOptions(configForm, effectivePrimary ?? undefined, params.modelCatalog)}
            </select>
          </label>
          <label class="field" style="min-width: 260px; flex: 1;">
            <span>Fallbacks</span>
            <select
              ?disabled=${(!configForm && !isDynamic) || configLoading || configSaving}
              @change=${(e: Event) => {
                const sel = e.target as HTMLSelectElement;
                const value = sel.value;
                if (!value) return;
                sel.value = "";
                const current = modelFallbacks ?? [];
                if (!current.includes(value)) {
                  onModelFallbacksChange(agent.id, [...current, value]);
                }
              }}
            >
              <option value="">Add fallback model…</option>
              ${buildFallbackOptions(configForm, effectivePrimary, modelFallbacks, params.modelCatalog)}
            </select>
            ${
              modelFallbacks && modelFallbacks.length > 0
                ? html`
                    <div class="fallback-tags" style="display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px;">
                      ${modelFallbacks.map(
                        (fb, idx) => html`
                          <span
                            class="fallback-tag"
                            style="display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 4px; background: var(--surface-2, #e8e8e8); font-size: 0.85em;"
                          >
                            <span style="opacity: 0.5; font-size: 0.8em; margin-right: 2px;">${idx + 1}.</span>
                            ${fb}
                            <button
                              type="button"
                              style="background: none; border: none; cursor: pointer; padding: 0 2px; font-size: 1.1em; line-height: 1; opacity: 0.6; color: inherit;"
                              title="Remove fallback"
                              ?disabled=${configLoading || configSaving}
                              @click=${() => {
                                const updated = (modelFallbacks ?? []).filter((_, i) => i !== idx);
                                onModelFallbacksChange(agent.id, updated);
                              }}
                            >x</button>
                          </span>
                        `,
                      )}
                    </div>
                  `
                : nothing
            }
          </label>
        </div>
        ${
          isDynamic
            ? html`
                <div class="muted" style="margin-top: 8px; font-size: 0.85em">
                  Model changes for dynamic agents are saved to the database automatically.
                </div>
              `
            : html`
              <div class="row" style="justify-content: flex-end; gap: 8px;">
                <button
                  class="btn btn--sm"
                  ?disabled=${configLoading}
                  @click=${onConfigReload}
                >
                  Reload Config
                </button>
                <button
                  class="btn btn--sm primary"
                  ?disabled=${configSaving || !configDirty}
                  @click=${onConfigSave}
                >
                  ${configSaving ? "Saving…" : "Save"}
                </button>
              </div>
            `
        }
      </div>
    </section>
  `;
}

function renderAgentContextCard(context: AgentContext, subtitle: string) {
  return html`
    <section class="card">
      <div class="card-title">Agent Context</div>
      <div class="card-sub">${subtitle}</div>
      <div class="agents-overview-grid" style="margin-top: 16px;">
        <div class="agent-kv">
          <div class="label">Workspace</div>
          <div class="mono">${context.workspace}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Primary Model</div>
          <div class="mono">${context.model}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Identity Name</div>
          <div>${context.identityName}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Identity Emoji</div>
          <div>${context.identityEmoji}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Skills Filter</div>
          <div>${context.skillsLabel}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Default</div>
          <div>${context.isDefault ? "yes" : "no"}</div>
        </div>
      </div>
    </section>
  `;
}

type ChannelSummaryEntry = {
  id: string;
  label: string;
  accounts: ChannelAccountSnapshot[];
};

function resolveChannelLabel(snapshot: ChannelsStatusSnapshot, id: string) {
  const meta = snapshot.channelMeta?.find((entry) => entry.id === id);
  if (meta?.label) {
    return meta.label;
  }
  return snapshot.channelLabels?.[id] ?? id;
}

function resolveChannelEntries(snapshot: ChannelsStatusSnapshot | null): ChannelSummaryEntry[] {
  if (!snapshot) {
    return [];
  }
  const ids = new Set<string>();
  for (const id of snapshot.channelOrder ?? []) {
    ids.add(id);
  }
  for (const entry of snapshot.channelMeta ?? []) {
    ids.add(entry.id);
  }
  for (const id of Object.keys(snapshot.channelAccounts ?? {})) {
    ids.add(id);
  }
  const ordered: string[] = [];
  const seed = snapshot.channelOrder?.length ? snapshot.channelOrder : Array.from(ids);
  for (const id of seed) {
    if (!ids.has(id)) {
      continue;
    }
    ordered.push(id);
    ids.delete(id);
  }
  for (const id of ids) {
    ordered.push(id);
  }
  return ordered.map((id) => ({
    id,
    label: resolveChannelLabel(snapshot, id),
    accounts: snapshot.channelAccounts?.[id] ?? [],
  }));
}

const CHANNEL_EXTRA_FIELDS = ["groupPolicy", "streamMode", "dmPolicy"] as const;

function resolveChannelConfigValue(
  configForm: Record<string, unknown> | null,
  channelId: string,
): Record<string, unknown> | null {
  if (!configForm) {
    return null;
  }
  const channels = (configForm.channels ?? {}) as Record<string, unknown>;
  const fromChannels = channels[channelId];
  if (fromChannels && typeof fromChannels === "object") {
    return fromChannels as Record<string, unknown>;
  }
  const fallback = configForm[channelId];
  if (fallback && typeof fallback === "object") {
    return fallback as Record<string, unknown>;
  }
  return null;
}

function formatChannelExtraValue(raw: unknown): string {
  if (raw == null) {
    return "n/a";
  }
  if (typeof raw === "string" || typeof raw === "number" || typeof raw === "boolean") {
    return String(raw);
  }
  try {
    return JSON.stringify(raw);
  } catch {
    return "n/a";
  }
}

function resolveChannelExtras(
  configForm: Record<string, unknown> | null,
  channelId: string,
): Array<{ label: string; value: string }> {
  const value = resolveChannelConfigValue(configForm, channelId);
  if (!value) {
    return [];
  }
  return CHANNEL_EXTRA_FIELDS.flatMap((field) => {
    if (!(field in value)) {
      return [];
    }
    return [{ label: field, value: formatChannelExtraValue(value[field]) }];
  });
}

function summarizeChannelAccounts(accounts: ChannelAccountSnapshot[]) {
  let connected = 0;
  let configured = 0;
  let enabled = 0;
  for (const account of accounts) {
    const probeOk =
      account.probe && typeof account.probe === "object" && "ok" in account.probe
        ? Boolean((account.probe as { ok?: unknown }).ok)
        : false;
    const isConnected = account.connected === true || account.running === true || probeOk;
    if (isConnected) {
      connected += 1;
    }
    if (account.configured) {
      configured += 1;
    }
    if (account.enabled) {
      enabled += 1;
    }
  }
  return {
    total: accounts.length,
    connected,
    configured,
    enabled,
  };
}

function renderAgentChannels(params: {
  agent: AgentsListResult["agents"][number];
  defaultId: string | null;
  configForm: Record<string, unknown> | null;
  agentFilesList: AgentsFilesListResult | null;
  agentIdentity: AgentIdentityResult | null;
  snapshot: ChannelsStatusSnapshot | null;
  loading: boolean;
  error: string | null;
  lastSuccess: number | null;
  onRefresh: () => void;
}) {
  const context = buildAgentContext(
    params.agent,
    params.configForm,
    params.agentFilesList,
    params.defaultId,
    params.agentIdentity,
  );
  const entries = resolveChannelEntries(params.snapshot);
  const lastSuccessLabel = params.lastSuccess
    ? formatRelativeTimestamp(params.lastSuccess)
    : "never";
  return html`
    <section class="grid grid-cols-2">
      ${renderAgentContextCard(context, "Workspace, identity, and model configuration.")}
      <section class="card">
        <div class="row" style="justify-content: space-between;">
          <div>
            <div class="card-title">Channels</div>
            <div class="card-sub">Gateway-wide channel status snapshot.</div>
          </div>
          <button class="btn btn--sm" ?disabled=${params.loading} @click=${params.onRefresh}>
            ${params.loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        <div class="muted" style="margin-top: 8px;">
          Last refresh: ${lastSuccessLabel}
        </div>
        ${
          params.error
            ? html`<div class="callout danger" style="margin-top: 12px;">${params.error}</div>`
            : nothing
        }
        ${
          !params.snapshot
            ? html`
                <div class="callout info" style="margin-top: 12px">Load channels to see live status.</div>
              `
            : nothing
        }
        ${
          entries.length === 0
            ? html`
                <div class="muted" style="margin-top: 16px">No channels found.</div>
              `
            : html`
              <div class="list" style="margin-top: 16px;">
                ${entries.map((entry) => {
                  const summary = summarizeChannelAccounts(entry.accounts);
                  const status = summary.total
                    ? `${summary.connected}/${summary.total} connected`
                    : "no accounts";
                  const config = summary.configured
                    ? `${summary.configured} configured`
                    : "not configured";
                  const enabled = summary.total ? `${summary.enabled} enabled` : "disabled";
                  const extras = resolveChannelExtras(params.configForm, entry.id);
                  return html`
                    <div class="list-item">
                      <div class="list-main">
                        <div class="list-title">${entry.label}</div>
                        <div class="list-sub mono">${entry.id}</div>
                      </div>
                      <div class="list-meta">
                        <div>${status}</div>
                        <div>${config}</div>
                        <div>${enabled}</div>
                        ${
                          extras.length > 0
                            ? extras.map((extra) => html`<div>${extra.label}: ${extra.value}</div>`)
                            : nothing
                        }
                      </div>
                    </div>
                  `;
                })}
              </div>
            `
        }
      </section>
    </section>
  `;
}

function renderAgentCron(params: {
  agent: AgentsListResult["agents"][number];
  defaultId: string | null;
  configForm: Record<string, unknown> | null;
  agentFilesList: AgentsFilesListResult | null;
  agentIdentity: AgentIdentityResult | null;
  jobs: CronJob[];
  status: CronStatus | null;
  loading: boolean;
  error: string | null;
  projectCronJobs: CronJobEntry[];
  projectCronJobsLoading: boolean;
  onRefresh: () => void;
}) {
  const context = buildAgentContext(
    params.agent,
    params.configForm,
    params.agentFilesList,
    params.defaultId,
    params.agentIdentity,
  );
  const gwJobs = params.jobs.filter((job) => job.agentId === params.agent.id);
  const dbJobs = params.projectCronJobs;
  const totalJobs = gwJobs.length + dbJobs.length;

  return html`
    <section class="grid grid-cols-2">
      ${renderAgentContextCard(context, "Workspace and scheduling targets.")}
      <section class="card">
        <div class="row" style="justify-content: space-between;">
          <div>
            <div class="card-title">Scheduler</div>
            <div class="card-sub">Gateway cron status.</div>
          </div>
          <button class="btn btn--sm" ?disabled=${params.loading} @click=${params.onRefresh}>
            ${params.loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
        <div class="stat-grid" style="margin-top: 16px;">
          <div class="stat">
            <div class="stat-label">Enabled</div>
            <div class="stat-value">
              ${params.status ? (params.status.enabled ? "Yes" : "No") : "n/a"}
            </div>
          </div>
          <div class="stat">
            <div class="stat-label">Total Jobs</div>
            <div class="stat-value">${totalJobs}</div>
          </div>
          <div class="stat">
            <div class="stat-label">Next wake</div>
            <div class="stat-value">${formatNextRun(params.status?.nextWakeAtMs ?? null)}</div>
          </div>
        </div>
        ${
          params.error
            ? html`<div class="callout danger" style="margin-top: 12px;">${params.error}</div>`
            : nothing
        }
      </section>
    </section>

    <!-- Gateway Cron Jobs -->
    <section class="card">
      <div class="card-title">Gateway Cron Jobs <span class="muted">(${gwJobs.length})</span></div>
      <div class="card-sub">Scheduled jobs from the gateway scheduler targeting this agent.</div>
      ${
        gwJobs.length === 0
          ? html`
              <div class="muted" style="margin-top: 16px">No gateway jobs assigned to this agent.</div>
            `
          : html`
              <div class="list" style="margin-top: 16px;">
                ${gwJobs.map(
                  (job) => html`
                  <div class="list-item">
                    <div class="list-main">
                      <div class="list-title">${job.name}</div>
                      ${job.description ? html`<div class="list-sub">${job.description}</div>` : nothing}
                      <div class="chip-row" style="margin-top: 6px;">
                        <span class="chip">${formatCronSchedule(job)}</span>
                        <span class="chip ${job.enabled ? "chip-ok" : "chip-warn"}">
                          ${job.enabled ? "enabled" : "disabled"}
                        </span>
                        <span class="chip">${job.sessionTarget}</span>
                      </div>
                    </div>
                    <div class="list-meta">
                      <div class="mono">${formatCronState(job)}</div>
                      <div class="muted">${formatCronPayload(job)}</div>
                    </div>
                  </div>
                `,
                )}
              </div>
            `
      }
    </section>

    <!-- Project Cron Jobs -->
    <section class="card" style="margin-top: 16px;">
      <div class="card-title">Project Cron Jobs <span class="muted">(${dbJobs.length})</span></div>
      <div class="card-sub">DB-backed cron jobs from projects managed by this agent.</div>
      ${
        params.projectCronJobsLoading
          ? html`
              <div class="muted" style="margin-top: 12px">Loading...</div>
            `
          : nothing
      }
      ${
        dbJobs.length === 0 && !params.projectCronJobsLoading
          ? html`
              <div class="muted" style="margin-top: 16px">No project cron jobs for this agent.</div>
            `
          : nothing
      }
      ${
        dbJobs.length > 0
          ? html`
              <div class="list" style="margin-top: 16px;">
                ${dbJobs.map(
                  (job) => html`
                  <div class="list-item">
                    <div class="list-main">
                      <div class="list-title">${job.name}</div>
                      <div class="list-sub">${job.projectName ?? job.projectId}</div>
                      <div class="chip-row" style="margin-top: 6px;">
                        <span class="chip">${job.cronExpression}</span>
                        <span class="chip ${job.isActive ? "chip-ok" : "chip-warn"}">
                          ${job.isActive ? "active" : "paused"}
                        </span>
                        <span class="chip">${job.trigger.replace(/_/g, " ")}</span>
                      </div>
                    </div>
                    <div class="list-meta">
                      <div class="muted">${job.timezone}</div>
                      ${job.nextRunAt ? html`<div class="muted">Next: ${job.nextRunAt.split("T")[0]}</div>` : nothing}
                    </div>
                  </div>
                `,
                )}
              </div>
            `
          : nothing
      }
    </section>

    <div class="callout info" style="margin-top: 12px;">
      Use the Cron tab in the sidebar to create and manage cron jobs.
    </div>
  `;
}

// --------------- Section Guard Visualization ---------------

type SectionRegion = {
  kind: "core" | "agent" | "skill" | "plain";
  label?: string;
  lines: string[];
};

const CORE_START_RE = /<!--\s*CORE\s+SECTION\s+START\s*-->/i;
const CORE_END_RE = /<!--\s*CORE\s+SECTION\s+END\s*-->/i;
const AGENT_START_RE = /<!--\s*AGENT\s+REAL\s+SOUL\s+START\s*-->/i;
const AGENT_END_RE = /<!--\s*AGENT\s+REAL\s+SOUL\s+END\s*-->/i;
const SKILL_OPEN_RE = /<!--\s*SKILL\s+OPEN:\s*(\S+)\s*-->/i;
const SKILL_CLOSE_RE = /<!--\s*SKILL\s+CLOSE:\s*\S+\s*-->/i;

function parseSections(content: string): SectionRegion[] {
  const lines = content.split("\n");
  const regions: SectionRegion[] = [];
  let current: SectionRegion = { kind: "plain", lines: [] };

  for (const line of lines) {
    if (CORE_START_RE.test(line)) {
      if (current.lines.length > 0) {
        regions.push(current);
      }
      current = { kind: "core", label: "CORE SECTION", lines: [line] };
    } else if (CORE_END_RE.test(line)) {
      current.lines.push(line);
      regions.push(current);
      current = { kind: "plain", lines: [] };
    } else if (AGENT_START_RE.test(line)) {
      if (current.lines.length > 0) {
        regions.push(current);
      }
      current = { kind: "agent", label: "AGENT EDITABLE", lines: [line] };
    } else if (AGENT_END_RE.test(line)) {
      current.lines.push(line);
      regions.push(current);
      current = { kind: "plain", lines: [] };
    } else if (SKILL_OPEN_RE.test(line)) {
      if (current.lines.length > 0) {
        regions.push(current);
      }
      const match = SKILL_OPEN_RE.exec(line);
      current = { kind: "skill", label: `SKILL: ${match?.[1] ?? ""}`, lines: [line] };
    } else if (SKILL_CLOSE_RE.test(line)) {
      current.lines.push(line);
      regions.push(current);
      current = { kind: "plain", lines: [] };
    } else {
      current.lines.push(line);
    }
  }
  if (current.lines.length > 0) {
    regions.push(current);
  }
  return regions;
}

function hasSectionMarkers(content: string): boolean {
  return CORE_START_RE.test(content) || AGENT_START_RE.test(content) || SKILL_OPEN_RE.test(content);
}

const SECTION_COLORS: Record<SectionRegion["kind"], string> = {
  core: "rgba(239,68,68,0.12)", // red
  agent: "rgba(34,197,94,0.12)", // green
  skill: "rgba(59,130,246,0.12)", // blue
  plain: "transparent",
};

const SECTION_BORDER_COLORS: Record<SectionRegion["kind"], string> = {
  core: "rgba(239,68,68,0.4)",
  agent: "rgba(34,197,94,0.4)",
  skill: "rgba(59,130,246,0.4)",
  plain: "transparent",
};

function renderSectionLegend() {
  return html`
    <div style="display:flex;gap:16px;align-items:center;margin-top:12px;font-size:0.8em;">
      <span style="font-weight:600;">Section Guard:</span>
      <span style="display:flex;align-items:center;gap:4px;">
        <span style="width:12px;height:12px;border-radius:2px;background:${SECTION_COLORS.core};border:1px solid ${SECTION_BORDER_COLORS.core};"></span>
        Core (locked)
      </span>
      <span style="display:flex;align-items:center;gap:4px;">
        <span style="width:12px;height:12px;border-radius:2px;background:${SECTION_COLORS.agent};border:1px solid ${SECTION_BORDER_COLORS.agent};"></span>
        Agent (editable)
      </span>
      <span style="display:flex;align-items:center;gap:4px;">
        <span style="width:12px;height:12px;border-radius:2px;background:${SECTION_COLORS.skill};border:1px solid ${SECTION_BORDER_COLORS.skill};"></span>
        Skill (managed)
      </span>
    </div>
  `;
}

function renderSectionPreview(content: string) {
  if (!hasSectionMarkers(content)) {
    return nothing;
  }
  const regions = parseSections(content);
  return html`
    <div style="margin-top:8px;border:1px solid var(--border);border-radius:6px;overflow:hidden;max-height:300px;overflow-y:auto;font-family:var(--font-mono,monospace);font-size:0.75em;line-height:1.5;">
      ${regions.map(
        (region) => html`
          <div style="background:${SECTION_COLORS[region.kind]};border-left:3px solid ${SECTION_BORDER_COLORS[region.kind]};padding:2px 8px;position:relative;">
            ${
              region.label
                ? html`<div style="font-size:0.85em;font-weight:600;color:${region.kind === "core" ? "var(--danger,#e55)" : region.kind === "agent" ? "var(--success,#3b3)" : "var(--accent,#59f)"};padding:2px 0;">${region.label}</div>`
                : nothing
            }
            <pre style="margin:0;white-space:pre-wrap;word-break:break-all;opacity:${region.kind === "plain" ? "0.6" : "0.85"};">${region.lines.join("\n")}</pre>
          </div>
        `,
      )}
    </div>
  `;
}

function renderCoreSectionWarning(baseContent: string, draft: string) {
  if (!hasSectionMarkers(baseContent)) {
    return nothing;
  }
  // Extract CORE section content from base and draft
  const extractCore = (text: string): string => {
    const regions = parseSections(text);
    return regions
      .filter((r) => r.kind === "core")
      .map((r) => r.lines.join("\n"))
      .join("\n");
  };
  const baseCore = extractCore(baseContent);
  const draftCore = extractCore(draft);
  if (baseCore === draftCore) {
    return nothing;
  }
  return html`
    <div class="callout danger" style="margin-top: 8px; font-size: 0.85em">
      Warning: You have modified a CORE SECTION region. The section guard will reject this save at the
      gateway level. Only edit content in AGENT (green) regions.
    </div>
  `;
}

// --------------- Agent Files Panel ---------------

function renderAgentFiles(params: {
  agentId: string;
  agentFilesList: AgentsFilesListResult | null;
  agentFilesLoading: boolean;
  agentFilesError: string | null;
  agentFileActive: string | null;
  agentFileContents: Record<string, string>;
  agentFileDrafts: Record<string, string>;
  agentFileSaving: boolean;
  onLoadFiles: (agentId: string) => void;
  onSelectFile: (name: string) => void;
  onFileDraftChange: (name: string, content: string) => void;
  onFileReset: (name: string) => void;
  onFileSave: (name: string) => void;
}) {
  const list = params.agentFilesList?.agentId === params.agentId ? params.agentFilesList : null;
  const files = list?.files ?? [];
  const active = params.agentFileActive ?? null;
  const activeEntry = active ? (files.find((file) => file.name === active) ?? null) : null;
  const baseContent = active ? (params.agentFileContents[active] ?? "") : "";
  const draft = active ? (params.agentFileDrafts[active] ?? baseContent) : "";
  const isDirty = active ? draft !== baseContent : false;

  return html`
    <section class="card">
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">Core Files</div>
          <div class="card-sub">Bootstrap persona, identity, and tool guidance.</div>
        </div>
        <button
          class="btn btn--sm"
          ?disabled=${params.agentFilesLoading}
          @click=${() => params.onLoadFiles(params.agentId)}
        >
          ${params.agentFilesLoading ? "Loading…" : "Refresh"}
        </button>
      </div>
      ${list ? html`<div class="muted mono" style="margin-top: 8px;">Workspace: ${list.workspace}</div>` : nothing}
      ${
        params.agentFilesError
          ? html`<div class="callout danger" style="margin-top: 12px;">${
              params.agentFilesError
            }</div>`
          : nothing
      }
      ${
        !list
          ? html`
              <div class="callout info" style="margin-top: 12px">
                Load the agent workspace files to edit core instructions.
              </div>
            `
          : html`
              <div class="agent-files-grid" style="margin-top: 16px;">
                <div class="agent-files-list">
                  ${
                    files.length === 0
                      ? html`
                          <div class="muted">No files found.</div>
                        `
                      : files.map((file) =>
                          renderAgentFileRow(file, active, () => params.onSelectFile(file.name)),
                        )
                  }
                </div>
                <div class="agent-files-editor">
                  ${
                    !activeEntry
                      ? html`
                          <div class="muted">Select a file to edit.</div>
                        `
                      : html`
                          <div class="agent-file-header">
                            <div>
                              <div class="agent-file-title mono">${activeEntry.name}</div>
                              <div class="agent-file-sub mono">${activeEntry.path}</div>
                            </div>
                            <div class="agent-file-actions">
                              <button
                                class="btn btn--sm"
                                ?disabled=${!isDirty}
                                @click=${() => params.onFileReset(activeEntry.name)}
                              >
                                Reset
                              </button>
                              <button
                                class="btn btn--sm primary"
                                ?disabled=${params.agentFileSaving || !isDirty}
                                @click=${() => params.onFileSave(activeEntry.name)}
                              >
                                ${params.agentFileSaving ? "Saving…" : "Save"}
                              </button>
                            </div>
                          </div>
                          ${
                            activeEntry.missing
                              ? html`
                                  <div class="callout info" style="margin-top: 10px">
                                    This file is missing. Saving will create it in the agent workspace.
                                  </div>
                                `
                              : nothing
                          }
                          ${renderSectionLegend()}
                          ${renderSectionPreview(draft)}
                          ${renderCoreSectionWarning(baseContent, draft)}
                          <label class="field" style="margin-top: 12px;">
                            <span>Content</span>
                            <textarea
                              .value=${draft}
                              @input=${(e: Event) => {
                                const val = (e.target as HTMLTextAreaElement).value;
                                params.onFileDraftChange(activeEntry.name, val);
                              }}
                            ></textarea>
                          </label>
                        `
                  }
                </div>
              </div>
            `
      }
    </section>
  `;
}

function renderAgentFileRow(file: AgentFileEntry, active: string | null, onSelect: () => void) {
  const status = file.missing
    ? "Missing"
    : `${formatBytes(file.size)} · ${formatRelativeTimestamp(file.updatedAtMs ?? null)}`;
  return html`
    <button
      type="button"
      class="agent-file-row ${active === file.name ? "active" : ""}"
      @click=${onSelect}
    >
      <div>
        <div class="agent-file-name mono">${file.name}</div>
        <div class="agent-file-meta">${status}</div>
      </div>
      ${
        file.missing
          ? html`
              <span class="agent-pill warn">missing</span>
            `
          : nothing
      }
    </button>
  `;
}

function renderAgentTools(params: {
  agentId: string;
  configForm: Record<string, unknown> | null;
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  onProfileChange: (agentId: string, profile: string | null, clearAllow: boolean) => void;
  onOverridesChange: (agentId: string, alsoAllow: string[], deny: string[]) => void;
  onConfigReload: () => void;
  onConfigSave: () => void;
  agentDef: AgentDefinitionEntry | null;
}) {
  const isDynamic = Boolean(params.agentDef);
  const config = resolveAgentConfig(params.configForm, params.agentId);
  // For dynamic agents, read toolPolicy from the DB definition
  const dynamicTools =
    isDynamic && params.agentDef?.toolPolicy
      ? (params.agentDef.toolPolicy as Record<string, unknown>)
      : null;
  const agentTools = dynamicTools ?? config.entry?.tools ?? {};
  const globalTools = config.globalTools ?? {};
  const profile = agentTools.profile ?? globalTools.profile ?? "full";
  const profileSource = agentTools.profile
    ? isDynamic
      ? "agent definition"
      : "agent override"
    : globalTools.profile
      ? "global default"
      : "default";
  const toolAllow = Array.isArray(agentTools.allow) ? (agentTools.allow as string[]) : [];
  const toolDeny = Array.isArray(agentTools.deny) ? (agentTools.deny as string[]) : [];
  const toolAlsoAllow = Array.isArray(agentTools.alsoAllow)
    ? (agentTools.alsoAllow as string[])
    : [];
  const hasAgentAllow = toolAllow.length > 0;
  const hasGlobalAllow = Array.isArray(globalTools.allow) && globalTools.allow.length > 0;
  const editable =
    isDynamic ||
    (Boolean(params.configForm) && !params.configLoading && !params.configSaving && !hasAgentAllow);
  const alsoAllow = hasAgentAllow ? [] : toolAlsoAllow;
  const deny = hasAgentAllow ? [] : toolDeny;
  const basePolicy = hasAgentAllow
    ? { allow: toolAllow, deny: toolDeny }
    : (resolveToolProfilePolicy(profile as string) ?? undefined);
  const toolIds = TOOL_SECTIONS.flatMap((section) => section.tools.map((tool) => tool.id));

  const resolveAllowed = (toolId: string) => {
    const baseAllowed = isAllowedByPolicy(toolId, basePolicy);
    const extraAllowed = matchesList(toolId, alsoAllow);
    const denied = matchesList(toolId, deny);
    const allowed = (baseAllowed || extraAllowed) && !denied;
    return {
      allowed,
      baseAllowed,
      denied,
    };
  };
  const enabledCount = toolIds.filter((toolId) => resolveAllowed(toolId).allowed).length;

  const updateTool = (toolId: string, nextEnabled: boolean) => {
    const nextAllow = new Set(
      alsoAllow.map((entry) => normalizeToolName(entry)).filter((entry) => entry.length > 0),
    );
    const nextDeny = new Set(
      deny.map((entry) => normalizeToolName(entry)).filter((entry) => entry.length > 0),
    );
    const baseAllowed = resolveAllowed(toolId).baseAllowed;
    const normalized = normalizeToolName(toolId);
    if (nextEnabled) {
      nextDeny.delete(normalized);
      if (!baseAllowed) {
        nextAllow.add(normalized);
      }
    } else {
      nextAllow.delete(normalized);
      nextDeny.add(normalized);
    }
    params.onOverridesChange(params.agentId, [...nextAllow], [...nextDeny]);
  };

  const updateAll = (nextEnabled: boolean) => {
    const nextAllow = new Set(
      alsoAllow.map((entry) => normalizeToolName(entry)).filter((entry) => entry.length > 0),
    );
    const nextDeny = new Set(
      deny.map((entry) => normalizeToolName(entry)).filter((entry) => entry.length > 0),
    );
    for (const toolId of toolIds) {
      const baseAllowed = resolveAllowed(toolId).baseAllowed;
      const normalized = normalizeToolName(toolId);
      if (nextEnabled) {
        nextDeny.delete(normalized);
        if (!baseAllowed) {
          nextAllow.add(normalized);
        }
      } else {
        nextAllow.delete(normalized);
        nextDeny.add(normalized);
      }
    }
    params.onOverridesChange(params.agentId, [...nextAllow], [...nextDeny]);
  };

  return html`
    <section class="card">
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">Tool Access</div>
          <div class="card-sub">
            Profile + per-tool overrides for this agent.
            <span class="mono">${enabledCount}/${toolIds.length}</span> enabled.
          </div>
        </div>
        <div class="row" style="gap: 8px;">
          <button
            class="btn btn--sm"
            ?disabled=${!editable}
            @click=${() => updateAll(true)}
          >
            Enable All
          </button>
          <button
            class="btn btn--sm"
            ?disabled=${!editable}
            @click=${() => updateAll(false)}
          >
            Disable All
          </button>
          ${
            isDynamic
              ? nothing
              : html`
                <button
                  class="btn btn--sm"
                  ?disabled=${params.configLoading}
                  @click=${params.onConfigReload}
                >
                  Reload Config
                </button>
                <button
                  class="btn btn--sm primary"
                  ?disabled=${params.configSaving || !params.configDirty}
                  @click=${params.onConfigSave}
                >
                  ${params.configSaving ? "Saving…" : "Save"}
                </button>
              `
          }
        </div>
      </div>

      ${
        isDynamic
          ? html`
              <div class="callout info" style="margin-top: 12px">
                Tool changes for dynamic agents are saved to the database automatically.
              </div>
            `
          : !params.configForm
            ? html`
                <div class="callout info" style="margin-top: 12px">
                  Load the gateway config to adjust tool profiles.
                </div>
              `
            : nothing
      }
      ${
        hasAgentAllow
          ? html`
              <div class="callout info" style="margin-top: 12px">
                This agent is using an explicit allowlist in config. Tool overrides are managed in the Config tab.
              </div>
            `
          : nothing
      }
      ${
        hasGlobalAllow
          ? html`
              <div class="callout info" style="margin-top: 12px">
                Global tools.allow is set. Agent overrides cannot enable tools that are globally blocked.
              </div>
            `
          : nothing
      }

      <div class="agent-tools-meta" style="margin-top: 16px;">
        <div class="agent-kv">
          <div class="label">Profile</div>
          <div class="mono">${profile}</div>
        </div>
        <div class="agent-kv">
          <div class="label">Source</div>
          <div>${profileSource}</div>
        </div>
        ${
          params.configDirty
            ? html`
                <div class="agent-kv">
                  <div class="label">Status</div>
                  <div class="mono">unsaved</div>
                </div>
              `
            : nothing
        }
      </div>

      <div class="agent-tools-presets" style="margin-top: 16px;">
        <div class="label">Quick Presets</div>
        <div class="agent-tools-buttons">
          ${PROFILE_OPTIONS.map(
            (option) => html`
              <button
                class="btn btn--sm ${profile === option.id ? "active" : ""}"
                ?disabled=${!editable}
                @click=${() => params.onProfileChange(params.agentId, option.id, true)}
              >
                ${option.label}
              </button>
            `,
          )}
          <button
            class="btn btn--sm"
            ?disabled=${!editable}
            @click=${() => params.onProfileChange(params.agentId, null, false)}
          >
            Inherit
          </button>
        </div>
      </div>

      <div class="agent-tools-grid" style="margin-top: 20px;">
        ${TOOL_SECTIONS.map(
          (section) =>
            html`
            <div class="agent-tools-section">
              <div class="agent-tools-header">${section.label}</div>
              <div class="agent-tools-list">
                ${section.tools.map((tool) => {
                  const { allowed } = resolveAllowed(tool.id);
                  return html`
                    <div class="agent-tool-row">
                      <div>
                        <div class="agent-tool-title mono">${tool.label}</div>
                        <div class="agent-tool-sub">${tool.description}</div>
                      </div>
                      <label class="cfg-toggle">
                        <input
                          type="checkbox"
                          .checked=${allowed}
                          ?disabled=${!editable}
                          @change=${(e: Event) =>
                            updateTool(tool.id, (e.target as HTMLInputElement).checked)}
                        />
                        <span class="cfg-toggle__track"></span>
                      </label>
                    </div>
                  `;
                })}
              </div>
            </div>
          `,
        )}
      </div>
    </section>
  `;
}

type SkillGroup = {
  id: string;
  label: string;
  skills: SkillStatusEntry[];
};

const SKILL_SOURCE_GROUPS: Array<{ id: string; label: string; sources: string[] }> = [
  { id: "workspace", label: "Workspace Skills", sources: ["igloobot-workspace"] },
  { id: "built-in", label: "Built-in Skills", sources: ["igloobot-bundled"] },
  { id: "installed", label: "Installed Skills", sources: ["igloobot-managed"] },
  { id: "extra", label: "Extra Skills", sources: ["igloobot-extra"] },
];

function groupSkills(skills: SkillStatusEntry[]): SkillGroup[] {
  const groups = new Map<string, SkillGroup>();
  for (const def of SKILL_SOURCE_GROUPS) {
    groups.set(def.id, { id: def.id, label: def.label, skills: [] });
  }
  const builtInGroup = SKILL_SOURCE_GROUPS.find((group) => group.id === "built-in");
  const other: SkillGroup = { id: "other", label: "Other Skills", skills: [] };
  for (const skill of skills) {
    const match = skill.bundled
      ? builtInGroup
      : SKILL_SOURCE_GROUPS.find((group) => group.sources.includes(skill.source));
    if (match) {
      groups.get(match.id)?.skills.push(skill);
    } else {
      other.skills.push(skill);
    }
  }
  const ordered = SKILL_SOURCE_GROUPS.map((group) => groups.get(group.id)).filter(
    (group): group is SkillGroup => Boolean(group && group.skills.length > 0),
  );
  if (other.skills.length > 0) {
    ordered.push(other);
  }
  return ordered;
}

function renderAgentSkills(params: {
  agentId: string;
  report: SkillStatusReport | null;
  loading: boolean;
  error: string | null;
  activeAgentId: string | null;
  configForm: Record<string, unknown> | null;
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  filter: string;
  onFilterChange: (next: string) => void;
  onRefresh: () => void;
  onToggle: (agentId: string, skillName: string, enabled: boolean) => void;
  onClear: (agentId: string) => void;
  onDisableAll: (agentId: string) => void;
  onConfigReload: () => void;
  onConfigSave: () => void;
  agentDef: AgentDefinitionEntry | null;
}) {
  const isDynamic = Boolean(params.agentDef);
  const editable =
    isDynamic || (Boolean(params.configForm) && !params.configLoading && !params.configSaving);
  const config = resolveAgentConfig(params.configForm, params.agentId);
  // For dynamic agents, read skills from the DB definition
  const dynamicSkills =
    isDynamic && Array.isArray(params.agentDef?.skills) && params.agentDef!.skills.length > 0
      ? params.agentDef!.skills
      : undefined;
  const allowlist =
    dynamicSkills ?? (Array.isArray(config.entry?.skills) ? config.entry?.skills : undefined);
  const allowSet = new Set((allowlist ?? []).map((name) => name.trim()).filter(Boolean));
  const usingAllowlist = allowlist !== undefined;
  const reportReady = Boolean(params.report && params.activeAgentId === params.agentId);
  const rawSkills = reportReady ? (params.report?.skills ?? []) : [];
  const filter = params.filter.trim().toLowerCase();
  const filtered = filter
    ? rawSkills.filter((skill) =>
        [skill.name, skill.description, skill.source].join(" ").toLowerCase().includes(filter),
      )
    : rawSkills;
  const groups = groupSkills(filtered);
  const enabledCount = usingAllowlist
    ? rawSkills.filter((skill) => skill.globalActive !== false && allowSet.has(skill.name)).length
    : rawSkills.filter((skill) => skill.globalActive !== false).length;
  const totalCount = rawSkills.length;

  return html`
    <section class="card">
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">Skills</div>
          <div class="card-sub">
            Per-agent skill allowlist and workspace skills.
            ${totalCount > 0 ? html`<span class="mono">${enabledCount}/${totalCount}</span>` : nothing}
          </div>
        </div>
        <div class="row" style="gap: 8px;">
          <button class="btn btn--sm" ?disabled=${!editable} @click=${() => params.onClear(params.agentId)}>
            Use All
          </button>
          <button class="btn btn--sm" ?disabled=${!editable} @click=${() => params.onDisableAll(params.agentId)}>
            Disable All
          </button>
          ${
            isDynamic
              ? nothing
              : html`
                <button
                  class="btn btn--sm"
                  ?disabled=${params.configLoading}
                  @click=${params.onConfigReload}
                >
                  Reload Config
                </button>
              `
          }
          <button class="btn btn--sm" ?disabled=${params.loading} @click=${params.onRefresh}>
            ${params.loading ? "Loading…" : "Refresh"}
          </button>
          ${
            isDynamic
              ? nothing
              : html`
                <button
                  class="btn btn--sm primary"
                  ?disabled=${params.configSaving || !params.configDirty}
                  @click=${params.onConfigSave}
                >
                  ${params.configSaving ? "Saving…" : "Save"}
                </button>
              `
          }
        </div>
      </div>

      ${
        !params.configForm && !isDynamic
          ? html`
              <div class="callout info" style="margin-top: 12px">
                Load the gateway config to set per-agent skills.
              </div>
            `
          : nothing
      }
      ${
        isDynamic
          ? html`
              <div class="callout info" style="margin-top: 12px">
                ${
                  usingAllowlist
                    ? "This dynamic agent uses a custom skill allowlist. Changes are saved to the database automatically."
                    : "All skills are enabled. Toggling a skill will create a per-agent allowlist (saved to database)."
                }
              </div>
            `
          : usingAllowlist
            ? html`
                <div class="callout info" style="margin-top: 12px">This agent uses a custom skill allowlist.</div>
              `
            : html`
                <div class="callout info" style="margin-top: 12px">
                  All skills are enabled. Disabling any skill will create a per-agent allowlist.
                </div>
              `
      }
      ${
        !reportReady && !params.loading
          ? html`
              <div class="callout info" style="margin-top: 12px">
                Load skills for this agent to view workspace-specific entries.
              </div>
            `
          : nothing
      }
      ${
        params.error
          ? html`<div class="callout danger" style="margin-top: 12px;">${params.error}</div>`
          : nothing
      }

      <div class="filters" style="margin-top: 14px;">
        <label class="field" style="flex: 1;">
          <span>Filter</span>
          <input
            .value=${params.filter}
            @input=${(e: Event) => params.onFilterChange((e.target as HTMLInputElement).value)}
            placeholder="Search skills"
          />
        </label>
        <div class="muted">${filtered.length} shown</div>
      </div>

      ${
        filtered.length === 0
          ? html`
              <div class="muted" style="margin-top: 16px">No skills found.</div>
            `
          : html`
              <div class="agent-skills-groups" style="margin-top: 16px;">
                ${groups.map((group) =>
                  renderAgentSkillGroup(group, {
                    agentId: params.agentId,
                    allowSet,
                    usingAllowlist,
                    editable,
                    onToggle: params.onToggle,
                  }),
                )}
              </div>
            `
      }
    </section>
  `;
}

function renderAgentSkillGroup(
  group: SkillGroup,
  params: {
    agentId: string;
    allowSet: Set<string>;
    usingAllowlist: boolean;
    editable: boolean;
    onToggle: (agentId: string, skillName: string, enabled: boolean) => void;
  },
) {
  const collapsedByDefault = group.id === "workspace" || group.id === "built-in";
  return html`
    <details class="agent-skills-group" ?open=${!collapsedByDefault}>
      <summary class="agent-skills-header">
        <span>${group.label}</span>
        <span class="muted">${group.skills.length}</span>
      </summary>
      <div class="list skills-grid">
        ${group.skills.map((skill) =>
          renderAgentSkillRow(skill, {
            agentId: params.agentId,
            allowSet: params.allowSet,
            usingAllowlist: params.usingAllowlist,
            editable: params.editable,
            onToggle: params.onToggle,
          }),
        )}
      </div>
    </details>
  `;
}

function renderAgentSkillRow(
  skill: SkillStatusEntry,
  params: {
    agentId: string;
    allowSet: Set<string>;
    usingAllowlist: boolean;
    editable: boolean;
    onToggle: (agentId: string, skillName: string, enabled: boolean) => void;
  },
) {
  const globallyInactive = skill.globalActive === false;
  const enabled = globallyInactive
    ? false
    : params.usingAllowlist
      ? params.allowSet.has(skill.name)
      : true;
  const missing = [
    ...skill.missing.bins.map((b) => `bin:${b}`),
    ...skill.missing.env.map((e) => `env:${e}`),
    ...skill.missing.config.map((c) => `config:${c}`),
    ...skill.missing.os.map((o) => `os:${o}`),
  ];
  const reasons: string[] = [];
  if (globallyInactive) {
    reasons.push("globally disabled in Skills Server");
  } else if (skill.disabled) {
    reasons.push("disabled");
  }
  if (skill.blockedByAllowlist) {
    reasons.push("blocked by allowlist");
  }
  return html`
    <div class="list-item agent-skill-row">
      <div class="list-main">
        <div class="list-title">
          ${skill.emoji ? `${skill.emoji} ` : ""}${skill.name}
        </div>
        <div class="list-sub">${skill.description}</div>
        <div class="chip-row" style="margin-top: 6px;">
          <span class="chip">${skill.source}</span>
          <span class="chip ${skill.eligible && !globallyInactive ? "chip-ok" : "chip-warn"}">
            ${skill.eligible && !globallyInactive ? "eligible" : "blocked"}
          </span>
          ${
            globallyInactive
              ? html`
                  <span class="chip chip-warn">globally disabled</span>
                `
              : skill.disabled
                ? html`
                    <span class="chip chip-warn">disabled</span>
                  `
                : nothing
          }
        </div>
        ${
          missing.length > 0
            ? html`<div class="muted" style="margin-top: 6px;">Missing: ${missing.join(", ")}</div>`
            : nothing
        }
        ${
          reasons.length > 0
            ? html`<div class="muted" style="margin-top: 6px;">Reason: ${reasons.join(", ")}</div>`
            : nothing
        }
      </div>
      <div class="list-meta">
        <label class="cfg-toggle">
          <input
            type="checkbox"
            .checked=${enabled}
            ?disabled=${!params.editable || globallyInactive}
            @change=${(e: Event) =>
              params.onToggle(params.agentId, skill.name, (e.target as HTMLInputElement).checked)}
          />
          <span class="cfg-toggle__track"></span>
        </label>
      </div>
    </div>
  `;
}
